import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Toolbar,
  AppBar,
  Typography,
  CssBaseline,
  Button,
  LinearProgress,
  Collapse,
  IconButton,
  Tooltip,
  Badge,
  InputBase,
  Menu,
  MenuItem,
  GlobalStyles,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  People as PeopleIcon,
  Chat as ChatIcon,
  ReceiptLong as ReceiptLongIcon,
  Description as DescriptionIcon,
  FactCheck as FactCheckIcon,
  Notifications as NotificationsIcon,
  EmojiEvents as EmojiEventsIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Menu as MenuIcon,
  ContactSupport as ContactSupportIcon,
  ReportProblem as ReportProblemIcon,
} from "@mui/icons-material";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import SchoolIcon from '@mui/icons-material/School';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TopicIcon from '@mui/icons-material/Topic';
import QuizIcon from '@mui/icons-material/Quiz';
import BarChartIcon from '@mui/icons-material/BarChart';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CategoryIcon from '@mui/icons-material/Category';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import AddIcon from '@mui/icons-material/Add';
import ListIcon from '@mui/icons-material/List';
import RateReviewIcon from '@mui/icons-material/RateReview';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import StorageIcon from '@mui/icons-material/Storage';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import StyleIcon from '@mui/icons-material/Style';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import HistoryIcon from '@mui/icons-material/History';
import FlagIcon from '@mui/icons-material/Flag';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import InsightsIcon from '@mui/icons-material/Insights';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CampaignIcon from '@mui/icons-material/Campaign';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import KeyIcon from '@mui/icons-material/Key';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SearchIcon from '@mui/icons-material/Search';
import EventIcon from '@mui/icons-material/Event';
import CoPresentIcon from '@mui/icons-material/CoPresent';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ApartmentIcon from '@mui/icons-material/Apartment';
import ExtensionIcon from '@mui/icons-material/Extension';
import PersonIcon from '@mui/icons-material/Person';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PanToolIcon from '@mui/icons-material/PanTool';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import WorkIcon from '@mui/icons-material/Work';
import MailIcon from '@mui/icons-material/Mail';
import SendIcon from '@mui/icons-material/Send';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import ManageHistoryIcon from '@mui/icons-material/ManageHistory';
import { Logout, Science as ScienceIcon, Settings as SettingsIcon } from "@mui/icons-material";
import CapitalizeFirstLetter from "../../CapitalizeFirstLetter";
import api from "../../api";
import { getAdminPermissionsFromStorage } from "../../utils/adminPermissions";
import { getTesterPermissionsFromStorage } from "../../utils/testerPermissions";
import { masterFormRoutePermissions } from "../../constants/masterFormPermissions";
import TesterPortalSwitcher from "../../components/TesterPortalSwitcher";
import AssistantDrawer from "../../components/PortalAssistant/AssistantDrawer";

const drawerWidth = 240;
const railWidth = 64;

const menuRowSx = (active) => ({
  minHeight: 34,
  px: 1.25,
  py: 0.5,
  mb: 0.25,
  borderRadius: "8px",
  color: active ? "#3B2A9F" : "#1F2937",
  "&.Mui-selected, &.Mui-selected:hover": { background: "#EDE9FE" },
  "&:hover": { background: "#F3F4F6" },
});
// Admin table standard (design item 8): one header look on every list page,
// readable cell spacing, and header text that never wraps. !important beats the
// per-page gradient headers; it only applies while the admin layout is mounted.
const adminTableStyles = {
  ".MuiTableHead-root, .MuiTableHead-root .MuiTableRow-root": {
    background: "#F9FAFB !important",
  },
  ".MuiTableHead-root .MuiTableCell-root": {
    background: "#F9FAFB !important",
    color: "#374151 !important",
    fontSize: "13px !important",
    fontWeight: "600 !important",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #E5E7EB !important",
    textShadow: "none !important",
  },
  ".MuiTableHead-root .MuiTableCell-sizeMedium": { padding: "11px 14px" },
  ".MuiTableHead-root .MuiTableSortLabel-root, .MuiTableHead-root .MuiTableSortLabel-root.Mui-active, .MuiTableHead-root .MuiTableSortLabel-icon, .MuiTableHead-root .MuiSvgIcon-root, .MuiTableHead-root .MuiCheckbox-root":
    { color: "#374151 !important" },
  ".MuiTableBody-root .MuiTableCell-root": {
    verticalAlign: "middle",
    borderBottomColor: "#F3F4F6",
  },
  ".MuiTableBody-root .MuiTableCell-sizeMedium": { padding: "10px 14px" },
  ".MuiTableBody-root .MuiTableRow-root:hover > .MuiTableCell-root": { backgroundColor: "#FAFAFF" },
  // Opt-in: the Actions column stays visible while a wide table scrolls sideways.
  ".admin-sticky-actions .MuiTableRow-root > .MuiTableCell-root:last-of-type": {
    position: "sticky",
    right: 0,
    zIndex: 1,
    background: "#fff",
    whiteSpace: "nowrap",
    boxShadow: "-6px 0 8px -6px rgba(17,24,39,0.15)",
  },
  ".admin-sticky-actions .MuiTableHead-root .MuiTableRow-root > .MuiTableCell-root:last-of-type": { zIndex: 2 },
};
const menuIconSx = (active) => ({ minWidth: 30, color: active ? "#3B2A9F" : "#6B7280", "& svg": { fontSize: 19 } });
const menuTextSx = (active) => ({ m: 0, "& .MuiTypography-root": { fontSize: 14, fontWeight: active ? 600 : 500 } });

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const loading = false;
  const [openMenus, setOpenMenus] = useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isCompactAppBar = useMediaQuery(theme.breakpoints.down("sm"));
  const appBarHeight = isCompactAppBar ? 56 : 70;
  const SIDEBAR_KEY = "admin-sidebar-open";
  const [sidebarOpen, setSidebarOpenState] = useState(true);
  const setSidebarOpen = (open) => {
    setSidebarOpenState(open);
    if (!isMobile) {
      try { localStorage.setItem(SIDEBAR_KEY, open ? "1" : "0"); } catch { /* storage blocked */ }
    }
  };

  useEffect(() => {
    let saved = null;
    try { saved = localStorage.getItem(SIDEBAR_KEY); } catch { /* storage blocked */ }
    setSidebarOpenState(isMobile ? false : saved !== "0");
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) setSidebarOpenState(false);
  }, [location.pathname, isMobile]);

  const [announcementCount, setAnnouncementCount] = useState(0);
  const isTesterRole = localStorage.getItem("role") === "tester";
  const [adminIdentity, setAdminIdentity] = useState(() => ({
    name: localStorage.getItem("admin-name") || "",
    role: localStorage.getItem("admin-role-name") || "",
  }));
  const [adminPermissions, setAdminPermissions] = useState(() =>
    isTesterRole
      ? (getTesterPermissionsFromStorage() ?? [])
      : (getAdminPermissionsFromStorage() ?? [])
  );

  // Sync from API so sidebar matches Spatie even if localStorage is missing/stale (e.g. session before this feature).
  useEffect(() => {
    if (process.env.REACT_APP_DISABLE_ADMIN_PERMISSIONS_SYNC === "true") {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Treat 404 as non-fatal: older / not-yet-deployed APIs omit GET api/admin/auth/permissions.
        const res = await api.get("admin/auth/permissions", {
          validateStatus: (status) => status === 200 || status === 404,
        });
        if (cancelled) {
          return;
        }
        if (res.status === 404) {
          return;
        }
        const data = res.data;
        if (!data?.success || !Array.isArray(data.data?.permissions)) {
          return;
        }
        const perms = data.data.permissions;
        const storageKey = isTesterRole ? "tester-permissions" : "admin-permissions";
        localStorage.setItem(storageKey, JSON.stringify(perms));
        setAdminPermissions(perms);
        if (data.data.full_name || data.data.role) {
          const identity = { name: data.data.full_name || "", role: data.data.role || "" };
          localStorage.setItem("admin-name", identity.name);
          localStorage.setItem("admin-role-name", identity.role);
          setAdminIdentity(identity);
        }
      } catch {
        // Keep cached permissions from login / localStorage
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isTesterRole]);

  const canViewParents = adminPermissions.includes("view-parents");
  const canViewParentsSubscriptions = adminPermissions.includes(
    "view-parents-subscriptions"
  );
  const canViewMobileUsers =
    adminPermissions.includes("view-mobile-users") || canViewParentsSubscriptions;
  const canViewStudents = adminPermissions.includes("view-students");
  const canCreateStudents = adminPermissions.includes("create-students");
  const canViewTutors = adminPermissions.includes("view-tutors");
  const canUpdateTutors = adminPermissions.includes("update-tutors");
  const canViewCertificates = adminPermissions.includes("view-certificates");
  const canViewAwards = adminPermissions.includes("view-awards");
  const canViewAnnouncements = adminPermissions.includes("view-announcements");
  const canAccessMockExamCategories =
    adminPermissions.includes("view-mock-exam-categories") ||
    adminPermissions.includes("create-mock-exam-categories") ||
    adminPermissions.includes("update-mock-exam-categories") ||
    adminPermissions.includes("delete-mock-exam-categories");
  const canViewMockExams = adminPermissions.includes("view-mock-exams");
  const canCreateMockExams = adminPermissions.includes("create-mock-exams");
  const canUpdateMockExams = adminPermissions.includes("update-mock-exams");
  const canDeleteMockExams = adminPermissions.includes("delete-mock-exams");
  const canAccessMockExamsMenu =
    canViewMockExams ||
    canCreateMockExams ||
    canUpdateMockExams ||
    canDeleteMockExams;
  const canAccessPaperCategories =
    adminPermissions.includes("view-paper-categories") ||
    adminPermissions.includes("create-paper-categories") ||
    adminPermissions.includes("update-paper-categories") ||
    adminPermissions.includes("delete-paper-categories");
  const canViewFlaggedQuestions = adminPermissions.includes(
    "view-flagged-questions"
  );
  const canViewVerificationDashboard =
    adminPermissions.includes("view-verification-dashboard") || adminPermissions.includes("verify-explanation_video");
  const canManageExamModes = adminPermissions.includes("manage-exam-modes");
  const canViewLeads = adminPermissions.includes("view-leads");
  const canSendWhatsApp = adminPermissions.includes("send-whatsapp-msg");
  const canViewSupportChat = adminPermissions.includes("view-support-chat");
  const canViewAuditTrail = adminPermissions.includes("view-audit-trail");
  const canManageRbac = adminPermissions.includes("manage-roles-permissions");
  const canViewRolesPage = canManageRbac && adminPermissions.includes("view-roles");
  const canViewPermissionsPage = canManageRbac && adminPermissions.includes("view-permissions");
  const canViewMobileHelpSupport = canViewLeads || canViewSupportChat;
  const canViewFlashCards = adminPermissions.includes("view-flash-cards");
  const canCreateFlashCards = adminPermissions.includes("create-flash-cards");

  const canViewPapers = adminPermissions.includes("view-papers");
  const canCreatePapers = adminPermissions.includes("create-papers");
  const canUpdatePapers = adminPermissions.includes("update-papers");
  const canDeletePapers = adminPermissions.includes("delete-papers");
  const canAccessPaperExtract =
    adminPermissions.includes("view-paper-extracts") ||
    adminPermissions.includes("create-paper-extracts") ||
    adminPermissions.includes("update-paper-extracts") ||
    adminPermissions.includes("delete-paper-extracts");
  const canViewPaperPurchases = adminPermissions.includes(
    "view-paper-purchases"
  );
  const canManageTutorPaperAccess = adminPermissions.includes(
    "manage-tutor-paper-access"
  );

  const canViewAssignments = adminPermissions.includes("view-assignments");
  const canCreateAssignments = adminPermissions.includes("create-assignments");
  const canUpdateAssignments = adminPermissions.includes("update-assignments");
  const canDeleteAssignments = adminPermissions.includes("delete-assignments");
  const canUseAiAssignmentCreator = adminPermissions.includes(
    "use-ai-assignment-creator"
  );

  const canViewQuestionBank = adminPermissions.includes("view-question-bank");
  const canCreateQuestionBank = adminPermissions.includes("create-question-bank");
  const canViewQuestionBankImports = adminPermissions.includes(
    "view-question-bank-imports"
  );
  const canCreateQuestionBankImports = adminPermissions.includes(
    "create-question-bank-imports"
  );
  const canViewQuestionBankPapers = adminPermissions.includes(
    "view-question-bank-papers"
  );
  const canViewQuestionBankSimilarPapers = adminPermissions.includes(
    "view-question-bank-similar-papers"
  );
  const canUpdateQuestionBankSimilarPapers = adminPermissions.includes(
    "update-question-bank-similar-papers"
  );

  const canViewGameTopics = adminPermissions.includes("view-game-topics");
  const canCreateGameTopics = adminPermissions.includes("create-game-topics");
  const canUpdateGameTopics = adminPermissions.includes("update-game-topics");
  const canDeleteGameTopics = adminPermissions.includes("delete-game-topics");
  const canViewGameContents = adminPermissions.includes("view-game-contents");
  const canCreateGameContents = adminPermissions.includes("create-game-contents");
  const canUpdateGameContents = adminPermissions.includes("update-game-contents");
  const canDeleteGameContents = adminPermissions.includes("delete-game-contents");

  const canViewCourses = adminPermissions.includes("view-courses");
  const canCreateCourses = adminPermissions.includes("create-courses");
  const canViewCourseTopics = adminPermissions.includes("view-course-topics");
  const canCreateCourseTopics = adminPermissions.includes("create-course-topics");
  const canViewCourseDurations = adminPermissions.includes("view-course-durations");
  const canCreateCourseDurations = adminPermissions.includes("create-course-durations");
  const canViewCourseTimeslots = adminPermissions.includes("view-course-timeslots");
  const canCreateCourseTimeslots = adminPermissions.includes("create-course-timeslots");
  const canUpdateCourseTimeslots = adminPermissions.includes("update-course-timeslots");
  const canDeleteCourseTimeslots = adminPermissions.includes("delete-course-timeslots");
  const canViewCourseReports = adminPermissions.includes("view-course-reports");
  const canViewAnalytics = adminPermissions.includes("view-analytics");
  const canAccessMasterForms = masterFormRoutePermissions().some((p) =>
    adminPermissions.includes(p)
  );

  const canAccessCourseTimeslotsSection =
    canViewCourseTimeslots ||
    canCreateCourseTimeslots ||
    canUpdateCourseTimeslots ||
    canDeleteCourseTimeslots;

  const name = process.env.REACT_APP_ADMIN_PREFIX;
  const isSuperAdminRole = (() => {
    try {
      const localRole = String(localStorage.getItem("role") || "")
        .toLowerCase()
        .trim();
      const rawUserData = localStorage.getItem("userData");
      const userData = rawUserData ? JSON.parse(rawUserData) : null;
      const userPortalRole = String(
        userData?.portal_role || userData?.role || ""
      )
        .toLowerCase()
        .trim();
      const role = userPortalRole || localRole;
      return role === "super admin" || role === "super_admin";
    } catch {
      return false;
    }
  })();
  const adminName = adminIdentity.name;
  const adminInitial = (adminName || "A").trim().charAt(0).toUpperCase();
  const adminRoleDisplay = adminIdentity.role
    ? CapitalizeFirstLetter(adminIdentity.role.replace(/[_-]+/g, " "))
    : isTesterRole ? "Tester" : isSuperAdminRole ? "Super Admin" : "Admin";
  // Which pre-designed questions the assistant offers (revenue is Super Admin only).
  const assistantStaffKind = isTesterRole ? "tester" : isSuperAdminRole ? "superadmin" : "admin";

  // Admin logout function
  const handleLogout = () => {
    // Remove admin-role from localStorage
    localStorage.removeItem('role');
    localStorage.removeItem('token'); // Also remove token if exists
    localStorage.removeItem('admin-permissions');
    localStorage.removeItem('admin-name');
    localStorage.removeItem('admin-role-name');
    window.location.href = "/login";
  };

  const handleToggle = (label) => {
    setOpenMenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  // How well a menu link matches the current URL: exact match beats any prefix,
  // a longer prefix beats a shorter one, -1 means no match. `also` lists extra
  // URLs the link owns (detail, edit and add pages).
  const matchScore = (item, path) => {
    let best = -1;
    [item.path, ...(item.also || [])].forEach((candidate) => {
      if (!candidate) return;
      if (path === candidate) best = Math.max(best, 10000 + candidate.length);
      else if (path.startsWith(`${candidate}/`)) best = Math.max(best, candidate.length);
    });
    return best;
  };

  const fetchUnreadCount = async () => {
    if (!canViewAnnouncements) {
      setAnnouncementCount(0);
      return;
    }
    try {
      const response = await api.get("admin/notifications/unread-announcement-count");
      if (response.data?.success && response.data?.data?.count != null) {
        setAnnouncementCount(Number(response.data.data.count) || 0);
      } else {
        setAnnouncementCount(0);
      }
    } catch {
      setAnnouncementCount(0);
    }
  };

  // When user opens Announcements page, mark all announcement notifications as read so badge shows 0
  useEffect(() => {
    if (!canViewAnnouncements) {
      setAnnouncementCount(0);
      return;
    }
    if (location.pathname.startsWith("/admin/announcements")) {
      api.post("admin/notifications/mark-all-announcements-read").then(() => {
        setAnnouncementCount(0);
      }).catch(() => {});
    } else {
      fetchUnreadCount();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, canViewAnnouncements]);

  // Two levels at most: section heading → item → sub-item. "Add …" pages are
  // buttons on their list pages; a user who may add but not list keeps the Add
  // link in place of the list so they do not lose access.
  const canViewCompetitiveIntel =
    canViewAnalytics ||
    adminPermissions.includes("view-master-form-schools") ||
    adminPermissions.includes("update-master-form-schools");
  const canReviewTutorContent = adminPermissions.includes("verify-tutor_resource");
  const listOrAdd = (canList, list, canAdd, add) =>
    canList ? [list] : canAdd ? [add] : [];
  const when = (allowed, ...items) => (allowed ? items : []);

  const menuSections = [
    {
      heading: "Overview",
      icon: <DashboardIcon />,
      items: [
        { label: "Dashboard", path: "/admin/dashboard", icon: <DashboardIcon /> },
        {
          label: "Analytics",
          icon: <InsightsIcon />,
          children: [
            ...when(canViewAnalytics,
              { label: "Reports", path: "/admin/analytics/batch-capacity", also: ["/admin/analytics"], icon: <AssessmentIcon /> },
              { label: "Marketing", path: "/admin/analytics/marketing", icon: <CampaignIcon /> },
            ),
            ...when(canViewCompetitiveIntel,
              { label: "Competitive Intelligence", path: "/admin/competitive-intel", icon: <TravelExploreIcon /> },
            ),
          ],
        },
      ],
    },
    {
      heading: "Content Library",
      icon: <LibraryBooksIcon />,
      items: [
        {
          label: "Question Bank",
          icon: <LibraryBooksIcon />,
          children: [
            ...listOrAdd(
              canViewQuestionBank,
              { label: "All Questions", path: "/admin/question-bank", icon: <ListIcon /> },
              canCreateQuestionBank,
              { label: "Add Question", path: "/admin/question-bank/create", icon: <AddIcon /> },
            ),
            ...when(canViewQuestionBankImports || canCreateQuestionBankImports,
              { label: "Import from PDF", path: "/admin/question-bank/import", icon: <UploadFileIcon /> },
            ),
            ...when(canViewQuestionBankImports,
              { label: "Recent Imports", path: "/admin/question-bank/recent-imports", icon: <HistoryIcon /> },
            ),
            ...when(canViewQuestionBankPapers,
              { label: "Papers (PDF)", path: "/admin/question-bank/papers", icon: <PictureAsPdfIcon /> },
            ),
            ...when(canViewQuestionBank,
              { label: "School Practice Papers", path: "/admin/question-bank/school-practice-papers", icon: <ApartmentIcon /> },
            ),
          ],
        },
        {
          label: "Worksheets",
          icon: <AssignmentIcon />,
          children: [
            ...when(canViewAssignments || canCreateAssignments || canUpdateAssignments || canDeleteAssignments,
              { label: "All Worksheets", path: `/${name}/test-list`, also: [`/${name}/course-test`], icon: <ListIcon /> },
            ),
            ...when(canUseAiAssignmentCreator || canCreateAssignments || canUpdateAssignments,
              { label: "AI Worksheet Creator", path: "/admin/ai-assignment-creator", icon: <AutoAwesomeIcon /> },
            ),
          ],
        },
        {
          label: "Mock Exams",
          icon: <QuizIcon />,
          children: [
            ...when(canAccessMockExamsMenu,
              { label: "All Mock Exams", path: "/admin/mock-exams", also: ["/admin/mock-exam"], icon: <ListIcon /> },
            ),
            ...when(canAccessMockExamCategories,
              { label: "Categories", path: "/admin/mock-exam-categories", icon: <CategoryIcon /> },
            ),
          ],
        },
        {
          label: "Papers",
          icon: <DescriptionIcon />,
          children: [
            ...when(canViewPapers || canCreatePapers || canUpdatePapers || canDeletePapers,
              { label: "All Papers", path: "/admin/papers", icon: <ListIcon /> },
            ),
            ...when(canAccessPaperExtract,
              { label: "Extract Paper", path: "/admin/mock-exams/paper-extract", icon: <FileDownloadIcon /> },
            ),
            ...when(canAccessPaperCategories,
              { label: "Categories", path: "/admin/paper-categories", icon: <CategoryIcon /> },
            ),
          ],
        },
        ...listOrAdd(
          canViewFlashCards,
          { label: "Flash Cards", path: "/admin/flash-cards", icon: <StyleIcon /> },
          canCreateFlashCards,
          { label: "Flash Cards", path: "/admin/flash-cards/add", icon: <StyleIcon /> },
        ),
        {
          label: "Games",
          icon: <SportsEsportsIcon />,
          children: [
            ...listOrAdd(
              canViewGameTopics || canUpdateGameTopics || canDeleteGameTopics,
              { label: "Game Topics", path: "/admin/game-topics", icon: <TopicIcon /> },
              canCreateGameTopics,
              { label: "Add Game Topic", path: "/admin/game-topics/add", icon: <AddIcon /> },
            ),
            ...listOrAdd(
              canViewGameContents || canUpdateGameContents || canDeleteGameContents,
              { label: "Game Content", path: "/admin/game-contents", icon: <ExtensionIcon /> },
              canCreateGameContents,
              { label: "Add Game Content", path: "/admin/game-contents/add", icon: <AddIcon /> },
            ),
          ],
        },
        ...when(canViewQuestionBank || canViewAssignments || canUpdateAssignments,
          { label: "Explanation Videos", path: "/admin/explanation-videos", icon: <VideoLibraryIcon /> },
        ),
      ],
    },
    {
      heading: "Quality & Review",
      icon: <VerifiedUserIcon />,
      items: [
        ...when(canViewVerificationDashboard,
          { label: "Verification Dashboard", path: "/admin/verification-dashboard", icon: <VerifiedUserIcon /> },
        ),
        ...when(canViewFlaggedQuestions,
          { label: "Flagged Questions", path: "/admin/flagged-questions", icon: <FlagIcon /> },
        ),
        ...when(canReviewTutorContent,
          { label: "Tutor Content Review", path: "/admin/tutor-resources", icon: <RateReviewIcon /> },
        ),
        ...when(canViewQuestionBankSimilarPapers || canUpdateQuestionBankSimilarPapers,
          { label: "Similar Papers", path: "/admin/question-bank/similar-papers", icon: <ContentCopyIcon /> },
        ),
        ...when(canViewMockExams || canViewPapers,
          { label: "Similarity Summary", path: "/admin/question-bank/similarity-summary", icon: <QueryStatsIcon /> },
        ),
        ...when(canViewQuestionBankImports,
          { label: "MCQ Conversions", path: "/admin/question-bank/mcq-review", icon: <FactCheckIcon /> },
        ),
      ],
    },
    {
      heading: "People",
      icon: <PeopleIcon />,
      items: [
        ...listOrAdd(
          canViewStudents,
          { label: "Students", path: "/admin/students", also: ["/admin/student"], icon: <PersonIcon /> },
          canCreateStudents,
          { label: "Students", path: "/admin/student/add", icon: <PersonIcon /> },
        ),
        ...when(canViewParents,
          { label: "Parents", path: "/admin/parents", icon: <PeopleIcon /> },
        ),
        ...when(canViewMobileUsers,
          { label: "Mobile Users", path: "/admin/mobile-users", icon: <PhoneIphoneIcon /> },
        ),
      ],
    },
    {
      heading: "Sales & Billing",
      icon: <ReceiptLongIcon />,
      items: [
        ...when(canViewParentsSubscriptions,
          { label: "Billing & Transactions", path: "/admin/billing-transactions", also: ["/admin/parent"], icon: <ReceiptLongIcon /> },
          { label: "Subscription Plans & Packs", path: "/admin/subscription-plans", icon: <CreditCardIcon /> },
          { label: "Mock Exam Purchases", path: "/admin/mock-exams/billing", icon: <ShoppingCartIcon /> },
        ),
        ...when(canViewPaperPurchases,
          { label: "Paper Purchases", path: "/admin/papers/billing", icon: <ShoppingBagIcon /> },
        ),
        ...when(canViewParentsSubscriptions,
          { label: "Parent Billing", path: "/admin/billing", also: ["/admin/course/billing"], icon: <RequestQuoteIcon /> },
        ),
      ],
    },
    {
      heading: "Leads",
      icon: <TrendingUpIcon />,
      items: [
        ...when(canViewLeads,
          { label: "Lead List", path: "/admin/leads", icon: <TrendingUpIcon /> },
          { label: "Tuition Requests", path: "/admin/tuition-requests", icon: <PanToolIcon /> },
          { label: "Course Waiting List", path: "/admin/course-waiting-list", icon: <HourglassBottomIcon /> },
          { label: "Tutor Applications", path: "/admin/tutor-applications", icon: <WorkIcon /> },
          { label: "Newsletter Subscribers", path: "/admin/subscriptions", icon: <MailIcon /> },
        ),
        ...when(canSendWhatsApp,
          { label: "WhatsApp Campaign", path: "/admin/whatsapp", icon: <SendIcon /> },
        ),
      ],
    },
    {
      heading: "Support",
      icon: <ChatIcon />,
      items: [
        ...when(canViewSupportChat,
          { label: "Support Chats", path: "/admin/support-chat", icon: <ChatIcon /> },
        ),
        ...when(canViewMobileHelpSupport,
          { label: "Mobile Help & Support", path: "/admin/mobile-help-support", icon: <ContactSupportIcon /> },
          { label: "Mobile App Issues", path: "/admin/mobile-app-issues", icon: <ReportProblemIcon /> },
        ),
        ...when(canViewLeads,
          { label: "Mobile Revision Support", path: "/admin/mobile-revision-support-requests", icon: <SupportAgentIcon /> },
        ),
        ...when(canViewSupportChat,
          { label: "Assistant Knowledge Base", path: "/admin/assistant-knowledge-base", icon: <MenuBookIcon /> },
        ),
      ],
    },
    {
      heading: "Engagement",
      icon: <NotificationsIcon />,
      items: [
        ...when(canViewAnnouncements,
          { label: "Announcements", path: "/admin/announcements", icon: <NotificationsIcon />, badge: true },
        ),
        ...when(canViewAwards, { label: "Awards", path: "/admin/awards", icon: <EmojiEventsIcon /> }),
        ...when(canViewCertificates, { label: "Certificates", path: "/admin/certificates", icon: <WorkspacePremiumIcon /> }),
        { label: "Testimonials", path: "/admin/testimonials", icon: <FormatQuoteIcon /> },
      ],
    },
    {
      heading: "System",
      icon: <SettingsIcon />,
      items: [
        ...when(canManageExamModes,
          { label: "Settings", path: "/admin/settings", icon: <SettingsIcon /> },
          { label: "Exam Modes", path: "/admin/exam-modes", icon: <ScienceIcon /> },
        ),
        ...when(canAccessMasterForms,
          { label: "Master Forms", path: "/admin/master-forms", icon: <StorageIcon /> },
          { label: "Display Order", path: "/admin/display-order", icon: <SwapVertIcon /> },
        ),
        {
          label: "Roles & Permissions",
          icon: <AdminPanelSettingsIcon />,
          children: [
            ...when(canViewRolesPage, { label: "Roles", path: "/admin/roles", icon: <ManageAccountsIcon /> }),
            ...when(canViewPermissionsPage, { label: "Permissions", path: "/admin/permissions", icon: <KeyIcon /> }),
          ],
        },
        ...when(canViewAuditTrail,
          { label: "Audit Trail", path: "/admin/audit-trail", icon: <ManageHistoryIcon /> },
        ),
        ...when(["admin", "super admin", "super_admin"].includes(String(localStorage.getItem("role") || "").toLowerCase().trim()),
          { label: "AI Prompts", path: "/admin/ai-prompts", icon: <AutoAwesomeIcon /> },
        ),
      ],
    },
    {
      heading: "Teaching & Delivery",
      icon: <SchoolIcon />,
      items: [
        {
          label: "Courses",
          icon: <SchoolIcon />,
          children: [
            ...listOrAdd(
              canViewCourses,
              { label: "All Courses", path: `/${name}/course-list`, also: [`/${name}/course`], icon: <ListIcon /> },
              canCreateCourses,
              { label: "Add Course", path: `/${name}/course`, icon: <AddIcon /> },
            ),
            ...listOrAdd(
              canViewCourseTopics || canCreateCourseTopics,
              { label: "Content", path: `/${name}/topic/subtopic-list`, also: [`/${name}/course-content`, `/${name}/topic`], icon: <TopicIcon /> },
              canCreateCourseTopics,
              { label: "Add Content", path: `/${name}/course-content`, icon: <AddIcon /> },
            ),
            ...listOrAdd(
              canViewCourseDurations || canViewAssignments || canUpdateAssignments || canDeleteAssignments,
              { label: "Durations", path: `/${name}/assignment-list`, also: [`/${name}/course-assignment`], icon: <AccessTimeIcon /> },
              canCreateCourseDurations || canCreateAssignments,
              { label: "Add Duration", path: `/${name}/course-assignment`, icon: <AddIcon /> },
            ),
            ...when(canAccessCourseTimeslotsSection,
              { label: "Timeslots", path: "/admin/timeslot", icon: <EventIcon /> },
            ),
            ...when(canViewCourseReports,
              { label: "Course Report", path: `/${name}/course-report`, icon: <BarChartIcon /> },
            ),
          ],
        },
        {
          label: "Tutors",
          icon: <CoPresentIcon />,
          children: [
            ...listOrAdd(
              canViewTutors,
              { label: "All Tutors", path: "/admin/tutors", also: ["/admin/tutor"], icon: <ListIcon /> },
              canUpdateTutors,
              { label: "Add Tutor", path: "/admin/tutor/add", icon: <AddIcon /> },
            ),
            ...when(canManageTutorPaperAccess,
              { label: "Paper Access Requests", path: "/admin/tutor-paper-requests", icon: <LockOpenIcon /> },
            ),
          ],
        },
        { label: "Resource Allocation", path: "/admin/resource-allocation", icon: <AssignmentTurnedInIcon /> },
        { label: "Holiday Management", path: "/admin/holiday-management", icon: <BeachAccessIcon /> },
      ],
    },
  ]
    .map((section) => ({
      ...section,
      // A group with no visible links is dropped; a section with no items is hidden.
      items: section.items.filter((item) => !item.children || item.children.length > 0),
    }))
    .filter((section) => section.items.length > 0);

  // Only the single best-matching link is highlighted.
  const activePath = (() => {
    let best = { score: -1, path: null };
    menuSections.forEach((section) =>
      section.items.forEach((item) =>
        [item, ...(item.children || [])].forEach((link) => {
          if (!link.path) return;
          const score = matchScore(link, location.pathname);
          if (score > best.score) best = { score, path: link.path };
        })
      )
    );
    return best.path;
  })();
  const groupHasActive = (item) => !!item.children?.some((c) => c.path === activePath);

  const [menuQuery, setMenuQuery] = useState("");
  const [railFlyout, setRailFlyout] = useState(null);
  const menuSearchRef = useRef(null);
  const query = menuQuery.trim().toLowerCase();
  const visibleSections = !query
    ? menuSections
    : menuSections
        .map((section) => ({
          ...section,
          items: section.items
            .map((item) => {
              if (!item.children) return item.label.toLowerCase().includes(query) ? item : null;
              if (item.label.toLowerCase().includes(query)) return item;
              const children = item.children.filter((c) => c.label.toLowerCase().includes(query));
              return children.length ? { ...item, children } : null;
            })
            .filter(Boolean),
        }))
        .filter((section) => section.items.length > 0);

  // Any table whose last column is "Action(s)" keeps that column pinned on the
  // right, so wide tables never hide their buttons off-screen.
  const mainRef = useRef(null);
  useEffect(() => {
    const root = mainRef.current;
    if (!root) return undefined;
    const tag = () => {
      root.querySelectorAll("table.MuiTable-root:not(.admin-sticky-actions)").forEach((table) => {
        const heads = table.querySelectorAll("thead tr:first-child > th");
        const last = heads[heads.length - 1];
        if (last && /^\s*actions?\s*$/i.test(last.textContent || "")) table.classList.add("admin-sticky-actions");
      });
    };
    tag();
    const observer = new MutationObserver(tag);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Ctrl/Cmd + K jumps to the menu search.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSidebarOpenState(true);
        setTimeout(() => menuSearchRef.current?.focus(), 0);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isMobile]);

  // Open the group that holds the current page (also on detail and edit pages).
  useEffect(() => {
    const open = {};
    menuSections.forEach((section) =>
      section.items.forEach((item) => {
        if (groupHasActive(item)) open[item.label] = true;
      })
    );
    setOpenMenus(open);
    setMenuQuery("");
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      height: "100vh",
      "@supports (height: 100dvh)": { height: "100dvh" },
      overflow: "hidden",
    }}>
      <CssBaseline />
      <GlobalStyles styles={adminTableStyles} />

      {/* Show loader only when loading is true */}
      {loading && <LinearProgress sx={{ height: 4 }} />}

      {/* Container for AppBar and main layout */}
      <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden", minHeight: 0 }}>
        {/* Fancy Top App Bar */}
        <AppBar
          position="fixed"
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            background: "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)",
            boxShadow: "none",
            top: loading ? "4px" : 0,
            transition: "top 0.3s",
          }}
        >
          <Toolbar sx={{
            justifyContent: "space-between",
            gap: 1,
            px: { xs: 1.5, sm: 2.5 },
            minHeight: { xs: 56, sm: 70 },
            height: { xs: 56, sm: 70 },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, minWidth: 0, flex: 1 }}>
              <Tooltip title={sidebarOpen ? "Hide menu" : "Show menu"} arrow>
                <IconButton
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  aria-label={sidebarOpen ? "Hide menu" : "Show menu"}
                  sx={{
                    background: "rgba(255,255,255,0.16)",
                    color: "#fff",
                    borderRadius: "10px",
                    "&:hover": { background: "rgba(255,255,255,0.26)" },
                  }}
                >
                  <MenuIcon sx={{ fontSize: 22 }} />
                </IconButton>
              </Tooltip>
              <Box
                component="button"
                type="button"
                onClick={() => navigate("/admin/dashboard")}
                aria-label="11 Plus Magic admin home"
                sx={{
                  background: "#fff",
                  border: 0,
                  borderRadius: "10px",
                  px: 1.5,
                  py: 0.75,
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <Box
                  component="img"
                  src={`${process.env.PUBLIC_URL || ""}/assets/images/logo.optimized.svg`}
                  alt="11 Plus Magic"
                  sx={{ height: { xs: 24, sm: 30 }, width: 'auto', display: 'block' }}
                />
              </Box>
              <Typography
                noWrap
                sx={{ color: "#fff", fontWeight: 600, fontSize: { xs: '1rem', sm: '1.125rem' } }}
              >
                Admin
              </Typography>
            </Box>

            {isTesterRole && <TesterPortalSwitcher />}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1.5 }, flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box
                  aria-hidden
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: "rgba(255,255,255,0.2)",
                    color: "#fff",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                  }}
                >
                  {adminInitial}
                </Box>
                <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', lineHeight: 1.2, maxWidth: 220 }}>
                  <Typography noWrap sx={{ color: "#fff", fontWeight: 600, fontSize: 14 }}>
                    {adminName || "Admin"}
                  </Typography>
                  <Typography noWrap sx={{ color: "rgba(255,255,255,0.85)", fontSize: 12 }}>
                    {adminRoleDisplay}
                  </Typography>
                </Box>
              </Box>

              <IconButton
                onClick={handleLogout}
                aria-label="Logout"
                sx={{
                  display: { xs: 'inline-flex', sm: 'none' },
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.5)",
                  borderRadius: "8px",
                }}
              >
                <Logout />
              </IconButton>
              <Button
                onClick={handleLogout}
                variant="outlined"
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.5)",
                  borderRadius: "8px",
                  px: 1.75,
                  height: 36,
                  fontWeight: 600,
                  fontSize: 14,
                  textTransform: 'none',
                  "&:hover": { borderColor: "#fff", background: "rgba(255,255,255,0.1)" },
                }}
              >
                Logout
              </Button>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Sidebar */}
        <Drawer
          variant={isMobile ? "temporary" : "permanent"}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            width: isMobile ? 0 : (sidebarOpen ? drawerWidth : railWidth),
            flexShrink: 0,
            alignSelf: "stretch",
            minHeight: 0,
            transition: 'width 0.3s ease',
            [`& .MuiDrawer-paper`]: {
              width: isMobile ? `min(${drawerWidth}px, 85vw)` : (sidebarOpen ? drawerWidth : railWidth),
              boxSizing: "border-box",
              mt: `${appBarHeight}px`,
              maxHeight: `calc(100vh - ${appBarHeight}px)`,
              // dvh so iOS Safari's bottom toolbar doesn't cover the end of the menu.
              "@supports (height: 100dvh)": { maxHeight: `calc(100dvh - ${appBarHeight}px)` },
              background: "#fff",
              borderRight: "1px solid #E5E7EB",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              transition: "all 0.3s ease",
            },
          }}
        >
          {!isMobile && !sidebarOpen ? (
            <Box component="nav" aria-label="Admin menu" sx={{ flex: 1, overflowY: "auto", py: 1.5, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, scrollbarWidth: "none" }}>
              {menuSections.map((section) => {
                const holdsActive = section.items.some((it) => it.path === activePath || groupHasActive(it));
                return (
                  <Tooltip key={section.heading} title={section.heading} placement="right" arrow>
                    <IconButton
                      aria-label={section.heading}
                      aria-haspopup="menu"
                      onClick={(e) => setRailFlyout({ anchor: e.currentTarget, section })}
                      sx={{
                        width: 44,
                        height: 40,
                        borderRadius: "10px",
                        color: holdsActive ? "#3B2A9F" : "#4B5563",
                        background: holdsActive ? "#EDE9FE" : "transparent",
                        "&:hover": { background: holdsActive ? "#EDE9FE" : "#F3F4F6" },
                        "& svg": { fontSize: 21 },
                      }}
                    >
                      {section.icon}
                    </IconButton>
                  </Tooltip>
                );
              })}
              <Menu
                anchorEl={railFlyout?.anchor}
                open={Boolean(railFlyout)}
                onClose={() => setRailFlyout(null)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                slotProps={{ paper: { sx: { minWidth: 220, ml: 1, borderRadius: 2 } } }}
              >
                {railFlyout && [
                  <Typography key="h" sx={{ fontSize: 11, fontWeight: 700, color: "#6B7280", letterSpacing: "0.06em", textTransform: "uppercase", px: 2, pt: 0.5, pb: 1 }}>
                    {railFlyout.section.heading}
                  </Typography>,
                  ...railFlyout.section.items.flatMap((item) =>
                    (item.children ? [{ ...item, header: true }, ...item.children.map((c) => ({ ...c, indent: true }))] : [item]).map((link) =>
                      link.header ? (
                        <Typography key={`g-${link.label}`} sx={{ fontSize: 13, fontWeight: 600, color: "#1F2937", px: 2, pt: 1, pb: 0.25 }}>
                          {link.label}
                        </Typography>
                      ) : (
                        <MenuItem
                          key={`${link.path}-${link.label}`}
                          selected={link.path === activePath}
                          onClick={() => {
                            setRailFlyout(null);
                            navigate(link.path);
                          }}
                          sx={{ fontSize: 14, pl: link.indent ? 3.5 : 2, minHeight: 34, "&.Mui-selected": { background: "#EDE9FE", color: "#3B2A9F", fontWeight: 600 } }}
                        >
                          {link.label}
                        </MenuItem>
                      )
                    )
                  ),
                ]}
              </Menu>
            </Box>
          ) : (
          <>
          <Box sx={{ p: "12px 12px 8px", flexShrink: 0 }}>
            <InputBase
              inputRef={menuSearchRef}
              value={menuQuery}
              onChange={(e) => setMenuQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setMenuQuery("");
              }}
              placeholder="Search menu…"
              inputProps={{ "aria-label": "Search the menu" }}
              startAdornment={<SearchIcon sx={{ fontSize: 18, color: "#6B7280", mr: 1 }} />}
              endAdornment={
                <Box component="span" sx={{ fontSize: 11, lineHeight: "16px", whiteSpace: "nowrap", flexShrink: 0, color: "#6B7280", border: "1px solid #E5E7EB", borderRadius: "4px", px: 0.5, display: { xs: "none", md: "inline" } }}>
                  Ctrl K
                </Box>
              }
              sx={{
                width: "100%",
                height: 36,
                border: "1px solid #D1D5DB",
                borderRadius: "8px",
                px: 1.25,
                fontSize: 14,
                color: "#111827",
                "&.Mui-focused": { borderColor: "#3B2A9F", boxShadow: "0 0 0 2px rgba(59,42,159,0.15)" },
              }}
            />
          </Box>
          <Box
            component="nav"
            aria-label="Admin menu"
            sx={{
              flex: 1,
              overflowY: "auto",
              px: 1.5,
              pb: 3,
              scrollbarWidth: "thin",
              scrollbarColor: "#C7C9D1 transparent",
            }}
          >
            {visibleSections.length === 0 && (
              <Typography sx={{ fontSize: 13, color: "#6B7280", px: 1.25, py: 2 }}>
                No menu item matches “{menuQuery.trim()}”.
              </Typography>
            )}
            {visibleSections.map((section) => (
              <Box key={section.heading} component="section" aria-label={section.heading}>
                <Typography
                  component="h2"
                  sx={{ fontSize: 11, fontWeight: 700, color: "#6B7280", letterSpacing: "0.06em", textTransform: "uppercase", p: "14px 10px 4px" }}
                >
                  {section.heading}
                </Typography>
                <List disablePadding>
                  {section.items.map((item) => {
                    if (!item.children) {
                      const active = item.path === activePath;
                      return (
                        <ListItemButton
                          key={item.label}
                          selected={active}
                          aria-current={active ? "page" : undefined}
                          onClick={() => navigate(item.path)}
                          sx={menuRowSx(active)}
                        >
                          <ListItemIcon sx={menuIconSx(active)}>
                            {item.badge ? (
                              <Badge badgeContent={announcementCount} color="error" max={99} invisible={announcementCount === 0 || location.pathname.startsWith("/admin/announcements")}>
                                {item.icon}
                              </Badge>
                            ) : (
                              item.icon
                            )}
                          </ListItemIcon>
                          <ListItemText primary={item.label} sx={menuTextSx(active)} />
                        </ListItemButton>
                      );
                    }
                    const containsActive = groupHasActive(item);
                    const open = !!query || !!openMenus[item.label];
                    return (
                      <Box key={item.label}>
                        <ListItemButton
                          onClick={() => handleToggle(item.label)}
                          aria-expanded={open}
                          sx={{ ...menuRowSx(false), color: containsActive ? "#3B2A9F" : "#1F2937" }}
                        >
                          <ListItemIcon sx={{ ...menuIconSx(false), color: containsActive ? "#3B2A9F" : "#6B7280" }}>
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={item.label}
                            sx={{ m: 0, "& .MuiTypography-root": { fontSize: 14, fontWeight: containsActive ? 600 : 500 } }}
                          />
                          {open ? <ExpandLess sx={{ fontSize: 18, color: "#6B7280" }} /> : <ExpandMore sx={{ fontSize: 18, color: "#6B7280" }} />}
                        </ListItemButton>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                          <List disablePadding>
                            {item.children.map((child) => {
                              const active = child.path === activePath;
                              return (
                                <ListItemButton
                                  key={child.label}
                                  selected={active}
                                  aria-current={active ? "page" : undefined}
                                  onClick={() => navigate(child.path)}
                                  sx={{ ...menuRowSx(active), minHeight: 32, pl: "38px" }}
                                >
                                  <ListItemText
                                    primary={child.label}
                                    sx={{ m: 0, "& .MuiTypography-root": { fontSize: 13, fontWeight: active ? 600 : 500, color: active ? "#3B2A9F" : "#374151" } }}
                                  />
                                </ListItemButton>
                              );
                            })}
                          </List>
                        </Collapse>
                      </Box>
                    );
                  })}
                </List>
              </Box>
            ))}
          </Box>
          </>
          )}
        </Drawer>
        {/* Main Content */}
        <Box
          component="main"
          ref={mainRef}
          sx={{
            flexGrow: 1,
            minHeight: 0,
            minWidth: 0,
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            p: { xs: 1, sm: 2, md: 3 },
            pb: 8, // Extra bottom padding to prevent content cropping
            mt: `${appBarHeight}px`,
            overflowY: "auto",
            overflowX: "hidden",
            // Custom scrollbar styling for main content
            "&::-webkit-scrollbar": {
              width: "10px",
            },
            "&::-webkit-scrollbar-track": {
              background: "rgba(0,0,0,0.05)",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "linear-gradient(180deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "10px",
              border: "2px solid #f9f9f9",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "linear-gradient(180deg, #3B2A9F 0%, #D62926 100%)",
            },
            // Firefox scrollbar
            scrollbarWidth: "thin",
            scrollbarColor: "#667eea rgba(0,0,0,0.05)",
          }}
        >
          <Box sx={{ pb: 4 }}>
            <Outlet />
          </Box>
        </Box>
      </Box>
      {adminPermissions.includes("use-ai-assistant") && <AssistantDrawer role="admin" staffKind={assistantStaffKind} />}
    </Box>
  );
};

export default AdminLayout;
